import { motion } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import "../style/ControlSection.css";
import { LayoutDashboard, TrendingUp, FileWarning, Bell, Mail, Building2 } from "lucide-react";

const ControlSection = () => {
    const scrollContainerRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);

    const features = [
        {
            icon: <LayoutDashboard style={{ color: "#2563EB", width: 'fit' }} />,
            title: "Dashboard & Reporting",
            description: "See sales over time, upcoming jobs, recent activity, and important business insights at a glance.",
            color: "#2563EB"
        },
        {
            icon: <TrendingUp style={{ color: "#FF8C00", width: 'fit' }} />,
            title: "Forecasting",
            description: "View future revenue and bookings with monthly and yearly breakdowns across all job statuses.",
            color: "#FF8C00"
        },
        {
            icon: <FileWarning style={{ color: "#1E40AF", width: 'fit' }} />,
            title: "Debt List",
            description: "Track all outstanding payments, see what's overdue, and download as Excel or PDF when needed.",
            color: "#1E40AF"
        },
        {
            icon: <Bell style={{ color: "#10B981", width: 'fit' }} />,
            title: "Internal Notifications",
            description: "Get platform alerts when customers view quotes, approve jobs, open invoices, make payments, and more.",
            color: "#10B981"
        },
        {
            icon: <Mail style={{ color: "#EC4899", width: 'fit' }} />,
            title: "Communication Settings",
            description: "Choose how customers receive updates — Email only, SMS only, or both — plus control template behaviour.",
            color: "#EC4899"
        },
        {
            icon: <Building2 style={{ color: "#06B6D4", width: 'fit' }} />,
            title: "Business & Finance Settings",
            description: "Manage your business info, VAT rates, inclusive/exclusive pricing, bank details, Stripe connection & accountancy software.",
            color: "#06B6D4"
        }
    ];

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const container = scrollContainerRef.current;
            if (!container) return;

            const scrollLeft = container.scrollLeft;
            const cardWidth = container.querySelector('.feature-card-control')?.offsetWidth || 0;
            const gap = isMobile ? 20 : 30;

            // Calculate which card is most visible
            const index = Math.round(scrollLeft / (cardWidth + gap));
            setActiveIndex(Math.min(Math.max(0, index), features.length - 1));
        };

        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            handleScroll(); // Initial call
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, [features.length, isMobile]);

    const scrollToCard = (index) => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const cardWidth = container.querySelector('.feature-card-control')?.offsetWidth || 0;
        const gap = isMobile ? 20 : 30;
        const scrollPosition = index * (cardWidth + gap);

        container.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
        });
    };

    const handlePrevious = () => {
        if (activeIndex > 0) {
            scrollToCard(activeIndex - 1);
        }
    };

    const handleNext = () => {
        if (activeIndex < features.length - 1) {
            scrollToCard(activeIndex + 1);
        }
    };

    const cardVariants = {
        hidden: { opacity: 0 },
        visible: (i) => ({
            opacity: 1,
            x: 0,
            transition: {
                delay: i * 0.1,
                duration: 0.5
            }
        })
    };

    return (
        <section className="control-section" style={{
            backgroundColor: "#f9fafb",
        }}>
            <div className="container">
                {/* Section Header */}
                <motion.div
                    className="control-header"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="section-title-control">Insights, Forecasting & Admin Tools</h2>
                    <p className="section-subtitle-control">
                        Plan ahead, track performance, and manage your business settings with ease.
                    </p>
                </motion.div>

                {/* Horizontal Scrollable Cards */}
                <div className="scroll-wrapper-control">
                    {/* Previous Button - Desktop Only */}
                    {!isMobile && (
                        <button
                            className={`nav-button-control nav-button-left-control ${activeIndex === 0 ? 'disabled' : ''}`}
                            onClick={handlePrevious}
                            disabled={activeIndex === 0}
                            aria-label="Previous card"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    )}

                    <motion.div
                        className="features-scroll-control"
                        ref={scrollContainerRef}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                    >
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                className="feature-card-control"
                                custom={index}
                                variants={cardVariants}
                                whileHover={{
                                    y: -10,
                                    transition: { duration: 0.3 }
                                }}
                            >
                                <div
                                    className="feature-icon-wrapper-control"
                                    style={{ backgroundColor: `${feature.color}20` }}
                                >
                                    <span className="feature-icon-control">
                                        {feature.icon}
                                    </span>
                                </div>
                                <h3 className="feature-title-control">{feature.title}</h3>
                                <p className="feature-description-control">{feature.description}</p>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Next Button - Desktop Only */}
                    {!isMobile && (
                        <button
                            className={`nav-button-control nav-button-right-control ${activeIndex === features.length - 1 ? 'disabled' : ''}`}
                            onClick={handleNext}
                            disabled={activeIndex === features.length - 1}
                            aria-label="Next card"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    )}

                    {/* Scroll Indicators (Bubbles) */}
                    <div className="scroll-indicators">
                        {features.map((_, index) => (
                            <button
                                key={index}
                                className={`scroll-bubble ${activeIndex === index ? 'active' : ''}`}
                                onClick={() => scrollToCard(index)}
                                aria-label={`Scroll to card ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ControlSection;